// Considered: RollupEdgeCasesSection / MemberRow / SphereRow — rejected because the sections are shells and the
// row molecules carry unlink or select affordances this read-mostly row must not have. See spec Reuse Audit.
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import type { StatusOutputDestination } from '../../../types/statusFactors';
import { formatWeight } from './formatWeight';
import MeasureSheetMolecule from '../MeasureSheet/MeasureSheetMolecule';
import type { LinkedMeasure } from '../MeasureSheet/MeasureAddSheet';
import UndoStripMolecule from '../UndoStrip/UndoStripMolecule';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useStatusOutputs } from './useStatusOutputs';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import './RollupUpwardCard.scss';
import { nonBlank } from '../../../utils/nonBlank';

export interface RollupUpwardCardProps {
  /** The sphere whose outgoing rollups the card lists. */
  sphereId: string;
  className?: string;
}

/** A destination the caller can only discover: the route omits the view-gated fields. */
const isDiscoverOnly = (row: StatusOutputDestination) => !row.factorId && !row.sphereType;

/** The row is an edge on the measure axis; a containment-only match has no edge, so no weight to show or edit. */
const isMeasureRow = (row: StatusOutputDestination) => row.via.includes('measure');

const RollupUpwardCard: React.FC<RollupUpwardCardProps> = ({ sphereId, className }) => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, refetch } = useStatusOutputs(sphereId);
  const rows = data?.outputs ?? [];
  const sphereTypes = useMemo(() => rows.flatMap((row) => (row.sphereType ? [row.sphereType] : [])), [rows]);
  const { resolved: schemaMetadata } = useSchemaMetadata(sphereTypes);
  /** The destination's kind as its schema's display label; `sphereType` is the schema `system_name`. */
  const kindLabel = (row: StatusOutputDestination) => (row.sphereType ? schemaMetadata.get(row.sphereType)?.displayName : undefined);
  /** Destinations listed more than once — one row per rollup of theirs this sphere feeds. Only those rows name their rollup. */
  const repeatedDestinations = useMemo(() => {
    const seen = new Set<string>();
    const repeated = new Set<string>();
    for (const row of rows) {
      if (seen.has(row.sphereId)) repeated.add(row.sphereId);
      seen.add(row.sphereId);
    }
    return repeated;
  }, [rows]);
  const rollupLabel = (row: StatusOutputDestination) =>
    nonBlank(row.factorName) ?? t('statusFactors.types.relatedSpheres.label');
  const graphClient = useApiClientsOrNull()?.graphClient;
  const logAnalyticsEvent = useAnalytics();
  const [viewing, setViewing] = useState<StatusOutputDestination | null>(null);
  const [adding, setAdding] = useState(false);
  const [linked, setLinked] = useState<LinkedMeasure | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoFailed, setUndoFailed] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openSheet = (row: StatusOutputDestination, event?: React.MouseEvent<HTMLElement>) => {
    triggerRef.current = event?.currentTarget ?? (document.activeElement as HTMLElement | null);
    setViewing(row);
  };

  const closeSheet = () => {
    setViewing(null);
    triggerRef.current?.focus();
  };

  const openAdd = (event: React.MouseEvent<HTMLElement>) => {
    triggerRef.current = event.currentTarget;
    setAdding(true);
  };

  const closeAdd = () => {
    setAdding(false);
    triggerRef.current?.focus();
  };

  const viewExisting = (destinationId: string) => {
    const row = rows.find((candidate) => candidate.sphereId === destinationId && isMeasureRow(candidate) && !isDiscoverOnly(candidate));
    setAdding(false);
    if (row) setViewing(row);
  };

  const handleLinked = (result: LinkedMeasure) => {
    setUndoFailed(false);
    setLinked(result);
    refetch();
  };

  const undo = async () => {
    if (!graphClient || !linked) return;
    setUndoing(true);
    setUndoFailed(false);
    try {
      await graphClient.delete(`/spheres/${linked.parentId}/factors/${linked.factorId}/members/${sphereId}`);
    } catch {
      setUndoFailed(true);
      setUndoing(false);
      return;
    }
    logAnalyticsEvent('rollup_upward_link_undone', undefined, {});
    setUndoing(false);
    setLinked(null);
    refetch();
  };

  const detailLine = (row: StatusOutputDestination): string => {
    if (isDiscoverOnly(row)) {
      return t('statusFactors.upward.row.hidden');
    }
    if (row.excluded) {
      return t('statusFactors.upward.row.excluded');
    }
    if (!isMeasureRow(row)) return t('statusFactors.upward.row.detail.containment');
    const sentence = row.via.includes('containment') ? 'both' : 'measure';
    const weight = formatWeight(row.weight, i18n.language);
    return row.linkedVia
      ? t(`statusFactors.upward.row.detail.${sentence}`, { weight, via: t(`statusFactors.upward.row.viaLabel.${row.linkedVia}`) })
      : t(`statusFactors.upward.row.detail.${sentence}NoVia`, { weight });
  };

  const renderBody = () => {
    if (loading && !data) {
      return (
        <div className="rollup-upward__loading">
          <SkeletonAtom height="2.5rem" />
          <SkeletonAtom height="2.5rem" />
        </div>
      );
    }
    if (error && !data) {
      return (
        <div className="rollup-upward__error">
          <MessageAtom severity="error" text={t('statusFactors.upward.error')} />
          <ButtonAtom variant="text" size="small" label={t('statusFactors.upward.retry')} onClick={refetch} />
        </div>
      );
    }
    if (rows.length === 0) {
      return <p className="rollup-upward__none">{t('statusFactors.upward.none')}</p>;
    }
    return (
      <>
        {rows.map((row) => (
          <div
            className={['rollup-upward__row', row.excluded ? 'rollup-upward__row--excluded' : ''].filter(Boolean).join(' ')}
            key={`${row.sphereId}:${row.factorId ?? ''}`}
          >
            <GlyphAtom variant="tile" tileSize="sm" glyph={isMeasureRow(row) ? 'lucide:layers' : 'lucide:network'} fallbackText={row.sphereName} />
            <div className="rollup-upward__text">
              <div className="rollup-upward__name-line">
                <span className="rollup-upward__name" title={row.sphereName}>
                  {row.sphereName}
                </span>
                {row.sphereType && !isDiscoverOnly(row) && <span className="rollup-upward__kind">{kindLabel(row)}</span>}
                {repeatedDestinations.has(row.sphereId) && !isDiscoverOnly(row) && (
                  <span className="rollup-upward__rollup">{t('statusFactors.upward.row.rollup', { rollup: rollupLabel(row) })}</span>
                )}
              </div>
              <span className="rollup-upward__detail">{detailLine(row)}</span>
            </div>
            {isMeasureRow(row) && !isDiscoverOnly(row) && (
              <ButtonAtom
                variant="text"
                size="small"
                className="rollup-upward__link"
                label={t('statusFactors.upward.view')}
                onClick={(e: React.MouseEvent<HTMLElement>) => openSheet(row, e)}
              />
            )}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className={['rollup-upward', className].filter(Boolean).join(' ')}>
      <section className="rollup-upward__card" aria-labelledby={`${sphereId}-rollup-upward-heading`}>
        <p className="rollup-upward__eyebrow">{t('statusFactors.upward.eyebrow')}</p>
        <header className="rollup-upward__header">
          <h3 className="rollup-upward__heading" id={`${sphereId}-rollup-upward-heading`}>
            {t('statusFactors.upward.heading')}
          </h3>
          <ButtonAtom variant="text" size="small" className="rollup-upward__link" label={t('statusFactors.upward.add')} onClick={openAdd} />
        </header>
        <div className="rollup-upward__body">
          {linked && (
            <UndoStripMolecule
              message={
                undoFailed
                  ? t('statusFactors.upward.undo.failed', { measure: linked.measureName })
                  : t('statusFactors.upward.undo.done', { measure: linked.measureName })
              }
              variant={undoFailed ? 'error' : 'default'}
              undoLabel={t('statusFactors.upward.undo.action')}
              dismissLabel={t('statusFactors.upward.undo.dismiss')}
              retryLabel={t('statusFactors.upward.retry')}
              onUndo={undo}
              onRetry={undo}
              onDismiss={() => setLinked(null)}
              busy={undoing}
              focusKey={linked.parentId}
            />
          )}
          {renderBody()}
          {rows.length > 0 && <p className="rollup-upward__footer">{t('statusFactors.upward.footer')}</p>}
        </div>
      </section>
      {adding && (
        <MeasureSheetMolecule
          mode="add"
          visible
          onHide={closeAdd}
          memberId={sphereId}
          existing={rows.filter((row) => row.via.includes('measure')).map((row) => ({ sphereId: row.sphereId, excluded: row.excluded }))}
          onViewExisting={viewExisting}
          onLinked={handleLinked}
        />
      )}
      {viewing && (
        <MeasureSheetMolecule
          key={`${viewing.sphereId}:${viewing.factorId ?? ''}`}
          visible
          onHide={closeSheet}
          parentId={viewing.sphereId}
          factorId={viewing.factorId}
          memberId={sphereId}
          measureName={viewing.sphereName}
          measureKind={kindLabel(viewing)}
          weight={viewing.weight}
          editable={viewing.editable === true}
          onSaved={refetch}
        />
      )}
    </div>
  );
};

export default RollupUpwardCard;
